import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchObstaclesByUnit,
  fetchObstaclesByLocation,
  fetchObstaclePresets,
  createObstacle,
  updateObstacle,
  deleteObstacle,
} from '@/lib/api/obstacles'
import {
  mapObstacleRow,
  mapPresetRow,
  type Obstacle,
  type CreateObstacleInput,
  type UpdateObstacleInput,
} from '@/types/obstacle'
import { useTenant } from '@/contexts'
import { toast } from 'sonner'

/**
 * Query key factory for obstacles
 */
export const obstacleKeys = {
  all: ['obstacles'] as const,
  lists: () => [...obstacleKeys.all, 'list'] as const,
  byUnit: (unitId: string) => [...obstacleKeys.lists(), 'unit', unitId] as const,
  byLocation: (locationId: string) => [...obstacleKeys.lists(), 'location', locationId] as const,
  presets: () => [...obstacleKeys.all, 'presets'] as const,
}

/**
 * Fetch obstacles for a specific unit
 */
export function useObstacles(unitId: string | undefined) {
  return useQuery({
    queryKey: obstacleKeys.byUnit(unitId ?? ''),
    queryFn: async () => {
      const rows = await fetchObstaclesByUnit(unitId!)
      return rows.map(mapObstacleRow)
    },
    enabled: !!unitId,
  })
}

/**
 * Fetch all obstacles for a location (all units)
 */
export function useObstaclesByLocation(locationId: string | undefined) {
  return useQuery({
    queryKey: obstacleKeys.byLocation(locationId ?? ''),
    queryFn: async () => {
      const rows = await fetchObstaclesByLocation(locationId!)
      return rows.map(mapObstacleRow)
    },
    enabled: !!locationId,
  })
}

/**
 * Fetch obstacle presets (system + org-specific)
 */
export function useObstaclePresets() {
  return useQuery({
    queryKey: obstacleKeys.presets(),
    queryFn: async () => {
      const rows = await fetchObstaclePresets()
      return rows.map(mapPresetRow)
    },
  })
}

/**
 * Obstacle CRUD mutations
 */
export function useObstacleMutations() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  const invalidate = (_unitId?: string, _locationId?: string) => {
    queryClient.invalidateQueries({ queryKey: obstacleKeys.lists() })
  }

  const create = useMutation({
    mutationFn: async (input: CreateObstacleInput) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')

      const insert: Record<string, unknown> = {
        organization_id: currentOrganization.id,
        location_id: input.locationId,
        unit_id: input.unitId,
        name: input.name,
        obstacle_type: input.obstacleType,
        mobility: input.mobility ?? 'static',
        shape: input.shape ?? 'rectangle',
        position_x: input.positionX,
        position_y: input.positionY,
        rotation: input.rotation ?? 0,
        width: input.width ?? null,
        depth: input.depth ?? null,
        radius: input.radius ?? null,
        height: input.height,
        elevation: input.elevation ?? 0,
        effective_from: input.effectiveFrom ?? null,
        effective_until: input.effectiveUntil ?? null,
        is_active: input.isActive ?? true,
        group_id: input.groupId ?? null,
        group_label: input.groupLabel ?? null,
        color: input.color ?? '#808080',
        opacity: input.opacity ?? 1.0,
        label_visible: input.labelVisible ?? true,
        participates_in_collision: input.participatesInCollision ?? true,
        clearance_buffer_ft: input.clearanceBufferFt ?? 0,
        notes: input.notes ?? null,
      }

      const row = await createObstacle(insert)
      return mapObstacleRow(row)
    },
    onSuccess: () => {
      invalidate()
      toast.success('Obstacle created')
    },
    onError: (error: Error) => {
      toast.error('Failed to create obstacle', { description: error.message })
    },
  })

  const update = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateObstacleInput }) => {
      const dbUpdate: Record<string, unknown> = {}

      if (data.name !== undefined) dbUpdate.name = data.name
      if (data.obstacleType !== undefined) dbUpdate.obstacle_type = data.obstacleType
      if (data.mobility !== undefined) dbUpdate.mobility = data.mobility
      if (data.shape !== undefined) dbUpdate.shape = data.shape
      if (data.positionX !== undefined) dbUpdate.position_x = data.positionX
      if (data.positionY !== undefined) dbUpdate.position_y = data.positionY
      if (data.rotation !== undefined) dbUpdate.rotation = data.rotation
      if (data.width !== undefined) dbUpdate.width = data.width
      if (data.depth !== undefined) dbUpdate.depth = data.depth
      if (data.radius !== undefined) dbUpdate.radius = data.radius
      if (data.height !== undefined) dbUpdate.height = data.height
      if (data.elevation !== undefined) dbUpdate.elevation = data.elevation
      if (data.effectiveFrom !== undefined) dbUpdate.effective_from = data.effectiveFrom
      if (data.effectiveUntil !== undefined) dbUpdate.effective_until = data.effectiveUntil
      if (data.isActive !== undefined) dbUpdate.is_active = data.isActive
      if (data.groupId !== undefined) dbUpdate.group_id = data.groupId
      if (data.groupLabel !== undefined) dbUpdate.group_label = data.groupLabel
      if (data.color !== undefined) dbUpdate.color = data.color
      if (data.opacity !== undefined) dbUpdate.opacity = data.opacity
      if (data.labelVisible !== undefined) dbUpdate.label_visible = data.labelVisible
      if (data.participatesInCollision !== undefined)
        dbUpdate.participates_in_collision = data.participatesInCollision
      if (data.clearanceBufferFt !== undefined)
        dbUpdate.clearance_buffer_ft = data.clearanceBufferFt
      if (data.notes !== undefined) dbUpdate.notes = data.notes
      if (data.status !== undefined) dbUpdate.status = data.status

      const row = await updateObstacle(id, dbUpdate)
      return mapObstacleRow(row)
    },
    onMutate: async ({ id, data }) => {
      // Cancel in-flight queries so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: obstacleKeys.lists() })

      // Snapshot all list queries for rollback
      const previousQueries = queryClient.getQueriesData<Obstacle[]>({
        queryKey: obstacleKeys.lists(),
      })

      // Optimistically update every cached list that contains this obstacle
      for (const [queryKey] of previousQueries) {
        queryClient.setQueryData<Obstacle[]>(queryKey, (old) =>
          old?.map((o) => (o.id === id ? { ...o, ...data } : o))
        )
      }

      return { previousQueries }
    },
    onError: (error: Error, _vars, context) => {
      // Rollback all list caches to pre-mutation snapshots
      if (context?.previousQueries) {
        for (const [queryKey, prev] of context.previousQueries) {
          queryClient.setQueryData(queryKey, prev)
        }
      }
      toast.error('Failed to update obstacle', { description: error.message })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: obstacleKeys.lists() })
    },
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await deleteObstacle(id)
    },
    onSuccess: () => {
      invalidate()
      toast.success('Obstacle deleted')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete obstacle', { description: error.message })
    },
  })

  const duplicate = useMutation({
    mutationFn: async (obstacle: Obstacle) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')

      const insert: Record<string, unknown> = {
        organization_id: currentOrganization.id,
        location_id: obstacle.locationId,
        unit_id: obstacle.unitId,
        name: `${obstacle.name} (copy)`,
        obstacle_type: obstacle.obstacleType,
        mobility: obstacle.mobility,
        shape: obstacle.shape,
        position_x: obstacle.positionX + 2,
        position_y: obstacle.positionY + 2,
        rotation: obstacle.rotation,
        width: obstacle.width,
        depth: obstacle.depth,
        radius: obstacle.radius,
        height: obstacle.height,
        elevation: obstacle.elevation,
        effective_from: obstacle.effectiveFrom,
        effective_until: obstacle.effectiveUntil,
        is_active: obstacle.isActive,
        group_id: obstacle.groupId,
        group_label: obstacle.groupLabel,
        color: obstacle.color,
        opacity: obstacle.opacity,
        label_visible: obstacle.labelVisible,
        participates_in_collision: obstacle.participatesInCollision,
        clearance_buffer_ft: obstacle.clearanceBufferFt,
        notes: obstacle.notes,
      }

      const row = await createObstacle(insert)
      return mapObstacleRow(row)
    },
    onSuccess: () => {
      invalidate()
      toast.success('Obstacle duplicated')
    },
    onError: (error: Error) => {
      toast.error('Failed to duplicate obstacle', { description: error.message })
    },
  })

  return { create, update, remove, duplicate }
}
